import { useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { queries } from '../graphql'
import { usePermissions } from './usePermissions'

/**
 * Hook to fetch menu items from Menu Management API
 * @param {string} menuType - Menu type (ADMIN, ADVISOR, PORTAL, DASHBOARD, ACTION)
 * @param {string} languageCode - Language code (default: 'en')
 * @returns {object} - { menuItems, loading, error }
 *
 * Note: For advisor impersonation, the backend filters NAVBAR items based on
 * customPermissions flags (menuGivingActivity, menuInvestmentActivity, menuContribute, menuDocuments).
 * Frontend only filters admin-level permissions (manage:access, system:admin) for non-ADMIN menus.
 * ADMIN menus rely on ProtectedMenuItem/ProtectedSection for permission filtering.
 */
export function useMenu(menuType, languageCode = 'en') {
  const { data, loading, error } = useQuery(queries.menu.menuItems, {
    variables: { menuType, languageCode },
    skip: !menuType,
  })

  const { hasAnyPermission, loading: permissionsLoading } = usePermissions()

  // Filter menu items by user permissions (only for admin-level permissions)
  const menuItems = useMemo(() => {
    if (!data?.menuItems) return []

    // For ADMIN menus, don't pre-filter - ProtectedMenuItem handles all permission checks
    // This allows granular permissions like user:view, grant:view, rbac:manage to work correctly
    if (menuType === 'ADMIN') {
      const sortItems = (items) =>
        items
          .map((item) => ({
            ...item,
            children: item.children ? sortItems(item.children) : [],
          }))
          .sort((a, b) => a.sortOrder - b.sortOrder)

      return sortItems(data.menuItems)
    }

    // Check if item requires admin-level permissions
    const hasRequiredPermissions = (item) => {
      const permissions = item.requiredPermissions || []
      // No requirements = show to everyone
      if (permissions.length === 0) return true

      // Only filter admin-level permissions on the frontend
      const adminPermissions = permissions.filter((p) => p === 'manage:access' || p === 'system:admin')
      if (adminPermissions.length === 0) return true

      // User must have at least one of the admin permissions
      return hasAnyPermission(adminPermissions)
    }

    // Filter items based on permissions and sort
    const filterAndSort = (items) =>
      items
        .filter(hasRequiredPermissions)
        .map((item) => ({
          ...item,
          children: item.children ? filterAndSort(item.children) : [],
        }))
        .sort((a, b) => a.sortOrder - b.sortOrder)

    return filterAndSort(data.menuItems)
  }, [data?.menuItems, hasAnyPermission, permissionsLoading, menuType])

  return {
    menuItems,
    // For ADMIN menus, permission filtering is handled per-item by ProtectedMenuItem/ProtectedSection,
    // so permissionsLoading should not gate the whole menu — it would unmount all sections on every
    // permission re-check, causing the intermittent disappearing-items bug.
    loading: loading || (menuType !== 'ADMIN' && permissionsLoading),
    error,
  }
}
